import { Injectable, OnDestroy, computed, inject, signal } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Subscription, filter, take } from 'rxjs';
import { NativeBridgeService } from './native-bridge.service';
import { WebViewAction } from '../../enums/webview-action';
import { PosthogService } from '../front/posthog.service';

export type PushPermissionStatus = 'granted' | 'denied' | 'notDetermined';

/** Réponse du natif, par `window.onPushPermission(<chaîne JSON>)`. */
export interface PushPermissionReply {
  status: PushPermissionStatus;
  platform: 'ios' | 'android';
}

/** « Plus tard » : on repropose une semaine après. */
export const PUSH_ASK_SNOOZE_MS = 7 * 24 * 60 * 60 * 1000;
/** Au-delà, le natif est tenu pour muet : on ne propose rien. */
export const PUSH_STATUS_TIMEOUT_MS = 3_000;

const SNOOZE_KEY = 'vw-push-ask-snoozed-until';
const ASKED_KEY = 'vw-push-asked';

/**
 * La permission des notifications, demandée au bon moment.
 *
 * Jusqu'à la 1.5.0, la coque la demandait au lancement, avant que l'app ait
 * rendu le moindre service, et la plupart refusaient. Depuis la 1.5.1, elle ne
 * la demande plus : c'est cette feuille qui le fait, juste après le premier
 * élément ajouté au jardin, quand une alerte de gel a un sens (CR-03).
 *
 * Contrat du canal `pushPermission` (voir lib/push/push_channel.dart) :
 * - le web envoie `status`, `request` ou `openSettings` ;
 * - le natif répond à `status` et `request` par `window.onPushPermission`
 *   avec `{ status, platform }`.
 *
 * Un binaire sans ce canal (1.5.0 et avant) a déjà posé la question au
 * lancement : rien n'est proposé (règle AD-03).
 */
@Injectable({ providedIn: 'root' })
export class PushPermissionService implements OnDestroy {
  private readonly bridge = inject(NativeBridgeService);
  private readonly router = inject(Router);
  private readonly posthog = inject(PosthogService);

  /** Dernier état connu ; `null` tant que le natif n'a rien dit. */
  readonly status = signal<PushPermissionStatus | null>(null);
  readonly platform = signal<'ios' | 'android' | null>(null);
  /** La feuille « Je te préviens la veille d'un gel ? » est ouverte. */
  readonly sheetOpen = signal(false);
  /** La question a déjà été posée par la feuille sur cet appareil. */
  private readonly asked = signal(readFlag(ASKED_KEY));

  /**
   * Alertes coupées par un refus : la ligne du Compte propose les réglages.
   *
   * Sur Android, « refusé » veut aussi dire « jamais demandé » (Android 13 et
   * plus, avant la question) : ce n'est un refus qu'après la feuille.
   */
  readonly alertsOff = computed(
    () =>
      this.status() === 'denied' &&
      (this.platform() === 'ios' || this.asked()),
  );

  /** Un élément vient d'être ajouté : proposition à l'écran suivant. */
  private pendingOffer?: Subscription;
  private readonly waiters: ((reply: PushPermissionReply | null) => void)[] = [];
  private readonly onVisible = () => {
    if (document.visibilityState === 'visible' && this.isAvailable()) {
      // Retour des réglages du téléphone : la ligne du Compte doit suivre.
      void this.refreshStatus();
    }
  };

  private readonly stopListening = this.bridge.listen('onPushPermission', (raw: unknown) =>
    this.receive(raw),
  );

  constructor() {
    document.addEventListener('visibilitychange', this.onVisible);
  }

  /** La coque sait-elle demander la permission ? (1.5.1 et suivantes) */
  isAvailable(): boolean {
    return this.bridge.has(WebViewAction.pushPermission);
  }

  /** Un élément du jardin vient d'être ajouté (plante, graine). */
  offerAfterGardenAdd(): void {
    if (!this.isAvailable() || this.pendingOffer) return;
    // Proposée sur l'écran d'ARRIVÉE (le potager, mes graines), pas par-dessus
    // le scanner ou l'assistant qui vient de se terminer.
    this.pendingOffer = this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        take(1),
      )
      .subscribe(() => {
        this.pendingOffer = undefined;
        void this.offerNow();
      });
  }

  /** Ouvre la feuille si la question peut et doit être posée maintenant. */
  async offerNow(): Promise<void> {
    if (!this.isAvailable() || this.sheetOpen() || this.isSnoozed()) return;
    const reply = await this.refreshStatus();
    if (!reply || !this.isAskable(reply)) return;
    this.sheetOpen.set(true);
  }

  /**
   * Appui sur une ligne « Active les alertes » (accueil) : la personne le
   * demande, on ignore donc le délai de « Plus tard ». Déjà accordée : rien ;
   * refusée pour de bon : les réglages du téléphone, seul endroit où la lever.
   */
  async askFromUser(): Promise<void> {
    if (!this.isAvailable() || this.sheetOpen()) return;
    const reply = await this.refreshStatus();
    if (!reply || reply.status === 'granted') return;
    if (this.isAskable(reply)) {
      this.sheetOpen.set(true);
    } else {
      this.openSettings();
    }
  }

  /** « Oui, préviens-moi » : la demande du système. */
  accept(): void {
    this.sheetOpen.set(false);
    this.asked.set(true);
    writeItem(ASKED_KEY, 'true');
    this.posthog.capture('push_permission', { granted: true, origin: 'sheet' });
    this.bridge.post(WebViewAction.pushPermission, 'request');
  }

  /** « Plus tard », ou la feuille refermée : on repropose dans 7 jours. */
  later(): void {
    if (!this.sheetOpen()) return;
    this.sheetOpen.set(false);
    writeItem(SNOOZE_KEY, String(Date.now() + PUSH_ASK_SNOOZE_MS));
    this.posthog.capture('push_permission', { granted: false, origin: 'sheet' });
  }

  /** Les réglages du téléphone, seul endroit où un refus se lève. */
  openSettings(): void {
    this.bridge.post(WebViewAction.pushPermission, 'openSettings');
  }

  /** Demande l'état au natif. `null` sans canal ou sans réponse à temps. */
  refreshStatus(): Promise<PushPermissionReply | null> {
    if (!this.isAvailable()) return Promise.resolve(null);
    return new Promise((resolve) => {
      const timer = setTimeout(() => {
        const index = this.waiters.indexOf(waiter);
        if (index !== -1) this.waiters.splice(index, 1);
        resolve(null);
      }, PUSH_STATUS_TIMEOUT_MS);
      const waiter = (reply: PushPermissionReply | null) => {
        clearTimeout(timer);
        resolve(reply);
      };
      this.waiters.push(waiter);
      this.bridge.post(WebViewAction.pushPermission, 'status');
    });
  }

  private isAskable(reply: PushPermissionReply): boolean {
    if (reply.status === 'notDetermined') return true;
    // Android 13+ : « refusé » tant que personne n'a demandé.
    return (
      reply.status === 'denied' &&
      reply.platform === 'android' &&
      !this.asked()
    );
  }

  private isSnoozed(): boolean {
    const until = Number(readItem(SNOOZE_KEY) ?? 0);
    return Number.isFinite(until) && Date.now() < until;
  }

  private receive(raw: unknown): void {
    let reply: PushPermissionReply;
    try {
      reply =
        typeof raw === 'string'
          ? JSON.parse(raw)
          : (raw as PushPermissionReply);
    } catch {
      console.warn('onPushPermission : charge illisible', raw);
      return;
    }
    if (!reply || !['granted', 'denied', 'notDetermined'].includes(reply.status)) {
      console.warn('onPushPermission : état inconnu', reply);
      return;
    }
    const previous = this.status();
    this.status.set(reply.status);
    this.platform.set(reply.platform === 'ios' ? 'ios' : 'android');
    // Réponse à la demande du système : on veut la mesurer.
    if (this.asked() && previous !== reply.status && reply.status !== 'notDetermined') {
      this.posthog.capture('push_permission', {
        granted: reply.status === 'granted',
        origin: 'system',
      });
    }
    this.waiters.splice(0).forEach((waiter) => waiter(reply));
  }

  ngOnDestroy(): void {
    this.pendingOffer?.unsubscribe();
    document.removeEventListener('visibilitychange', this.onVisible);
    this.stopListening();
  }
}

// Stockage local à l'abri : une WebView ou un navigateur privé peut le refuser.
function readItem(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function readFlag(key: string): boolean {
  return readItem(key) === 'true';
}

function writeItem(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    // Sans stockage, la feuille reviendra : un moindre mal.
  }
}
